import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, UserPlus, KeyRound, ToggleLeft, ToggleRight, Trash2, RefreshCw, Loader2, Crown, Mail, ScrollText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const StatusBadge = ({ active, superAdmin }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
    superAdmin ? 'bg-amber-50 text-amber-700 border border-amber-200'
    : active === false ? 'bg-rose-50 text-rose-700 border border-rose-200'
    : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
  }`}>
    {superAdmin ? 'Super Admin' : active === false ? 'Disabled' : 'Active'}
  </span>
);

const AdminUserManagement = () => {
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notAllowed, setNotAllowed] = useState(false);

  const [form, setForm] = useState({ email: '', fullName: '', password: '', phone: '' });
  const [newPassword, setNewPassword] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        axios.get(`${API}/admin/admins`, auth),
        axios.get(`${API}/admin/audit-log?limit=50`, auth),
      ]);
      setRows(a.data.admins || []);
      setAudit(l.data.entries || []);
      setNotAllowed(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setNotAllowed(true);
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!form.email || !form.fullName || !form.password) {
      toast.error('Email, full name, and password are all required');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/admins`, form, auth);
      toast.success(`Admin ${form.email} created`);
      setForm({ email: '', fullName: '', password: '', phone: '' });
      setShowCreate(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${API}/admin/admins/${row.id}`, { active: !(row.active !== false) }, auth);
      toast.success(row.active === false ? 'Admin re-enabled' : 'Admin disabled');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const doResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/admins/${resetTarget.id}/reset-password`, { newPassword }, auth);
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    }
  };

  const doDelete = async () => {
    try {
      await axios.delete(`${API}/admin/admins/${deleteTarget.id}`, auth);
      toast.success(`Admin ${deleteTarget.email} deleted`);
      setDeleteTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (notAllowed) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center">
        <ShieldCheck className="w-12 h-12 text-rose-500 mx-auto mb-3" />
        <h2 className="text-lg font-semibold">Super Admin only</h2>
        <p className="text-sm text-gray-600 mt-2">This module is restricted to the Super Admin. Regular admins cannot manage other admin accounts.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Admin User Management</h1>
            <p className="text-sm text-gray-500">Super-admin-only · create & manage named admin accounts</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="admins-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="admins-create-btn">
            <UserPlus className="w-4 h-4 mr-1" /> New Admin
          </Button>
        </div>
      </div>

      {/* Admins list */}
      <Card className="overflow-hidden mb-6">
        {loading ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No admin users yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      {r.superAdmin && <Crown className="w-4 h-4 text-amber-500" title="Super Admin" />}
                      {r.fullName || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">{r.email}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{r.phone || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge active={r.active} superAdmin={r.superAdmin} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{r.createdAt?.slice(0, 10) || '—'}</td>
                    <td className="px-4 py-3">
                      {!r.superAdmin ? (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => setResetTarget(r)} title="Reset password" className="text-indigo-600" data-testid={`admins-reset-${r.id}`}>
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => toggleActive(r)} title={r.active === false ? 'Enable' : 'Disable'} data-testid={`admins-toggle-${r.id}`}>
                            {r.active === false ? <ToggleLeft className="w-4 h-4 text-rose-500" /> : <ToggleRight className="w-4 h-4 text-emerald-600" />}
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(r)} title="Delete" className="text-gray-500 hover:text-rose-600" data-testid={`admins-delete-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Protected</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Audit log */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
          <ScrollText className="w-4 h-4 text-indigo-500" /> Recent activity
        </h3>
        {audit.length === 0 ? (
          <p className="text-sm text-gray-400">No activity yet.</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {audit.map(a => (
              <li key={a.id} className="flex items-start justify-between border-b border-gray-100 pb-2 last:border-0">
                <div>
                  <div className="font-medium text-gray-800">{a.actorEmail}</div>
                  <div className="text-gray-500">{a.action} → {a.targetEmail}</div>
                </div>
                <div className="text-gray-400 whitespace-nowrap">{a.timestamp?.slice(0, 19).replace('T', ' ')}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-500" /> Create New Admin
            </h3>
            <div className="space-y-3">
              <div>
                <Label>Full name *</Label>
                <Input value={form.fullName} onChange={(e) => setForm({...form, fullName: e.target.value})} data-testid="admins-form-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="admins-form-email" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="admins-form-phone" />
              </div>
              <div>
                <Label>Initial password * (min 8 chars)</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="admins-form-password" />
                <p className="text-xs text-gray-500 mt-1">Share this out-of-band; they can change it later via Forgot Password.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} data-testid="admins-form-submit">
                <Mail className="w-4 h-4 mr-1" /> Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-1">Reset password</h3>
            <p className="text-sm text-gray-600 mb-4">For <strong>{resetTarget.email}</strong></p>
            <Label>New password (min 8 chars)</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="admins-reset-input" />
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => { setResetTarget(null); setNewPassword(''); }}>Cancel</Button>
              <Button onClick={doResetPassword} data-testid="admins-reset-confirm">Reset</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Delete admin?</h3>
            <p className="text-sm text-gray-600 mb-5">
              This will permanently remove <strong>{deleteTarget.email}</strong>. They will no longer be able to log in.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="admins-delete-confirm">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
